/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import LinkButton from '@atlaskit/button/link';
import type { QuickInsertSelectionHandler } from '@atlaskit/editor-common/quick-insert/context';
import { QuickInsertProvider } from '@atlaskit/editor-common/quick-insert/provider';
import type { EmptyStateHandler } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import type { RegistryElementBrowserItem } from './model';

type Props = {
	editorView: EditorView;
	emptyStateHandler?: EmptyStateHandler;
	isOffline: boolean;
	items: RegistryElementBrowserItem[];
	onKeyDown: (event: React.KeyboardEvent) => void;
	onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onSelect: (key: string, handler: QuickInsertSelectionHandler) => void;
	query: string;
	resultsId: string;
	section?: string;
	selectedKey?: string;
};

const resultsGridStyles = css({
	display: 'grid',
	gap: token('space.100'),
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
});

const RegistryElementBrowserItem = ({
	editorView,
	isOffline,
	isSelected,
	item,
	onSelect,
}: {
	editorView: EditorView;
	isOffline: boolean;
	isSelected: boolean;
	item: RegistryElementBrowserItem;
	onSelect: (key: string, handler: QuickInsertSelectionHandler) => void;
}): React.JSX.Element | null => {
	const { component: Component, key } = item.registration;
	const contextValue = useMemo(
		() => ({
			editorView,
			isOffline,
			item: { id: key, isSelected },
			select: (handler: QuickInsertSelectionHandler) => onSelect(key, handler),
			surface: 'element-browser' as const,
		}),
		[editorView, isOffline, isSelected, key, onSelect],
	);

	if (!Component) {
		return null;
	}

	return (
		<QuickInsertProvider value={contextValue}>
			{React.createElement(Component, undefined, null)}
		</QuickInsertProvider>
	);
};

export const RegistryElementBrowserSearchResults = ({
	editorView,
	emptyStateHandler,
	isOffline,
	items,
	query,
	resultsId,
	section,
	selectedKey,
	onKeyDown,
	onSearchChange,
	onSelect,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.200">
			<Textfield
				autoFocus
				aria-controls={resultsId}
				aria-label={formatMessage({
					defaultMessage: 'Search elements',
					id: 'editor.quick-insert.search',
				})}
				name="registry-element-browser-search"
				onChange={onSearchChange}
				onKeyDown={onKeyDown}
				value={query}
			/>
			<Text aria-live="polite">
				{formatMessage(
					{ defaultMessage: '{count} results', id: 'editor.quick-insert.results' },
					{ count: items.length },
				)}
			</Text>
			<div
				aria-label={formatMessage({
					defaultMessage: 'Element results',
					id: 'editor.quick-insert.results-label',
				})}
				css={resultsGridStyles}
				data-testid="registry-element-browser-results"
				id={resultsId}
				onKeyDown={onKeyDown}
				tabIndex={-1}
				role={items.length ? 'listbox' : undefined}
			>
				{items.length
					? items.map((item) => (
							<RegistryElementBrowserItem
								editorView={editorView}
								isOffline={isOffline}
								isSelected={selectedKey === item.registration.key}
								item={item}
								key={item.registration.key}
								onSelect={onSelect}
							/>
						))
					: (emptyStateHandler?.({
							mode: 'full',
							searchTerm: query,
							selectedCategory: section,
						}) ?? (
							<Stack alignInline="center" space="space.100">
								<Text>
									{formatMessage({
										defaultMessage: 'Nothing matches your search',
										id: 'editor.quick-insert.empty',
									})}
								</Text>
								<LinkButton
									appearance="primary"
									href="https://marketplace.atlassian.com/search?category=Macros&hosting=cloud&product=confluence"
									target="_blank"
								>
									{formatMessage({
										defaultMessage: 'Explore Atlassian Marketplace',
										id: 'editor.quick-insert.explore-marketplace',
									})}
								</LinkButton>
							</Stack>
						))}
			</div>
		</Stack>
	);
};
